// One (enterprise, role) pair for the signed-in user — a user can hold a
// different role in every Enterprise they belong to (and within one
// Enterprise, a different role per Department/Team), so this is a flat
// list rather than a single role. See lib/auth/api.ts flattenEnterpriseRoles
// for how this is built from the login response's nested shape.
export type EnterpriseRoleEntry = {
  enterpriseId: string;
  enterpriseName: string;
  departmentId: string | null;
  departmentName: string | null;
  teamId: string | null;
  teamName: string | null;
  roleId: string;
  roleName: string;
  profileId: string;
  profileName: string;
};

export type FareAuthUser = {
  userId: string;
  username: string;
  userName: string;
  userEmail: string;
  userData?: Record<string, unknown>;
  enterpriseRoles?: EnterpriseRoleEntry[];
  // Which enterprise's role to treat as "current" app-wide — shared with
  // lib/lms/enterprise-api.ts getCurrentEnterpriseId/setCurrentEnterpriseId
  // (same single-enterprise concept Teams/Courses/Lessons already scope to).
  selectedEnterpriseId?: string;
};

const ACCESS_TOKEN_KEY = "fare_access_token";
const REFRESH_TOKEN_KEY = "fare_refresh_token";
const USER_KEY = "fare_user";

const LEGACY_TOKEN_KEY = "authToken";
const LEGACY_REFRESH_KEY = "refreshToken";
const LEGACY_USER_ID_KEY = "userId";
const LEGACY_USERNAME_KEY = "username";
const LEGACY_USER_NAME_KEY = "userName";
const LEGACY_USER_EMAIL_KEY = "userEmail";
const LEGACY_USER_DATA_KEY = "userData";

function canUseStorage() {
  return typeof window !== "undefined";
}

function migrateLegacySession() {
  if (!canUseStorage()) return;
  if (localStorage.getItem(ACCESS_TOKEN_KEY)) return;

  const token = sessionStorage.getItem(LEGACY_TOKEN_KEY);
  if (!token) return;

  localStorage.setItem(ACCESS_TOKEN_KEY, token);
  const refreshToken = sessionStorage.getItem(LEGACY_REFRESH_KEY);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

  const userId = sessionStorage.getItem(LEGACY_USER_ID_KEY) || "";
  const username = sessionStorage.getItem(LEGACY_USERNAME_KEY) || "";
  const userName = sessionStorage.getItem(LEGACY_USER_NAME_KEY) || username;
  const userEmail = sessionStorage.getItem(LEGACY_USER_EMAIL_KEY) || "";
  let userData: Record<string, unknown> | undefined;
  try {
    const raw = sessionStorage.getItem(LEGACY_USER_DATA_KEY);
    userData = raw ? (JSON.parse(raw) as Record<string, unknown>) : undefined;
  } catch {
    userData = undefined;
  }

  localStorage.setItem(
    USER_KEY,
    JSON.stringify({
      userId,
      username,
      userName,
      userEmail,
      userData,
    } satisfies FareAuthUser),
  );

  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_REFRESH_KEY);
}

export const storage = {
  getToken() {
    if (!canUseStorage()) return "";
    migrateLegacySession();
    return localStorage.getItem(ACCESS_TOKEN_KEY) || "";
  },
  setToken(token: string) {
    if (!canUseStorage()) return;
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  },
  getRefreshToken() {
    if (!canUseStorage()) return "";
    migrateLegacySession();
    return localStorage.getItem(REFRESH_TOKEN_KEY) || "";
  },
  setTokens(token: string, refreshToken?: string) {
    if (!canUseStorage()) return;
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },
  clear() {
    if (!canUseStorage()) return;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_REFRESH_KEY);
  },
  getUser<T = FareAuthUser>() {
    if (!canUseStorage()) return null;
    migrateLegacySession();
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },
  setUser(user: unknown) {
    if (!canUseStorage()) return;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
};
